import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { ScoreRing } from "../components/ui/ScoreRing";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import type { InterviewReport as ReportType } from "../types";

const SCORE_LABEL: Record<string, string> = {
  communication: "Communication",
  collaboration: "Collaboration",
  problemSolving: "Problem Solving",
  leadership: "Leadership",
};

export default function InterviewReport({ audience = "talent" }: { audience?: "talent" | "company" }) {
  const { id } = useParams<{ id: string }>();
  const { credits, subscription } = useAuth();

  const isPaid =
    subscription &&
    (subscription.plan === "pro_talent" || subscription.plan === "team_fit" || subscription.plan === "scale_up") &&
    (subscription.status === "active" || subscription.status === "trialing");

  const [report, setReport] = useState<ReportType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<ReportType>(`/api/interview/${id}/report?audience=${audience}`)
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load report."))
      .finally(() => setLoading(false));
  }, [id, audience]);

  return (
    <AppLayout title="Interview Report">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-navy">AI Interview Report</h1>
          <p className="text-xs text-slate-500">Comprehensive evaluation and feedback from your AI interview.</p>
        </div>
        {!isPaid && (
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              💳 Report Credits: {credits?.report_credits ?? 0}
            </span>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white transition hover:bg-navy-light"
            >
              ⚡ Upgrade Plan
            </Link>
          </div>
        )}
      </div>

      {!isPaid && (
        <Card className="mb-4 border border-teal/20 bg-gradient-to-r from-teal/5 to-cyan-500/5 !p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-navy">⚡ Unlock Unlimited AI Analysis & Full Coaching</p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Upgrade to Pro Talent to get unlimited interviews, report downloads, and team matching.
              </p>
            </div>
            <Link
              to="/pricing"
              className="rounded-lg bg-teal px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-teal-dark"
            >
              Upgrade to Pro →
            </Link>
          </div>
        </Card>
      )}

      {loading && <p className="text-xs text-slate-400">Loading report...</p>}
      {error && <Card className="border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}

      {report && (
        <>
          <Card className="border border-slate-100 text-center">
            <p className="text-xs font-medium text-slate-400">Overall Score</p>
            <div className="mt-2 flex justify-center">
              <ScoreRing value={report.overallScore} size={100} strokeWidth={9} />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">{report.summary}</p>
          </Card>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {Object.entries(report.scores).map(([key, value]) => (
              <Card key={key} className="border border-slate-100 text-center">
                <ScoreRing value={value} size={56} strokeWidth={6} />
                <p className="mt-1 text-[11px] text-slate-500">{SCORE_LABEL[key] ?? key}</p>
              </Card>
            ))}
          </div>

          <Card className="mt-4 border border-slate-100">
            <p className="text-sm font-semibold text-navy">Key Strengths</p>
            <ul className="mt-2 space-y-1.5">
              {report.strengths.map((s, i) => (
                <li key={i} className="flex gap-2 text-xs text-slate-600">
                  <span className="text-teal-dark">✓</span> {s}
                </li>
              ))}
            </ul>
          </Card>

          <Card className="mt-3 border border-slate-100">
            <p className="text-sm font-semibold text-navy">Areas for Improvement</p>
            <ul className="mt-2 space-y-1.5">
              {report.improvements.map((s, i) => (
                <li key={i} className="flex gap-2 text-xs text-slate-600">
                  <span className="text-coral">↑</span> {s}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </AppLayout>
  );
}
